import React, { ReactElement, Suspense, useEffect, useState } from 'react';

import styled from 'styled-components';

import {
  DocumentFields,
  DocumentsSections,
} from '@cd3p/core/modules/documents';
import { APIDocumentSearchResult, APISortOrder } from '@cd3p/core/types/api';
import { Typography } from '@mui/material';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { GlobalLoader, SvgIcon } from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';

import { docsComponents, filterContentByUserType } from './Components';

import { useSelector, useTranslation } from 'third-party';

import { useDocuments } from 'modules/documents';

import { appSelectors, documentsSelectors } from 'selectors';

const NavigationContainer = styled.div`
  min-width: 35rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: white;
  box-shadow: 10px 0 12px -10px rgba(0, 0, 0, 0.25);
`;

const ContentContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  min-height: 0;
`;

const HelpContainer = styled.div`
  margin: 2rem;
  width: 100%;
  overflow: scroll;
  display: flex;
  justify-content: center;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const StyledList = styled(List)<{ component?: React.ElementType }>`
  overflow-y: auto;
  flex-grow: 1;
  padding: 0;
`;

const PageHeader = styled(Typography)`
  color: ${props => props.theme.custom.palette.secondary700};
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.25);
  height: 2.5rem;
  padding: 2rem;
`;

const InnerContent = styled.div`
  max-width: 66.8rem;
  min-width: 66.8rem;
`;

type Props = {
  pageName: string;
  menuItemRenderer?: (it: any) => string | ReactElement | ReactElement[];
  documentsSection: DocumentsSections;
};

export const Docs: React.FC<Props> = ({
  pageName,
  menuItemRenderer,
  documentsSection,
}) => {
  const { t } = useTranslation();
  const handleApiResult = useHandleApiResult();
  const documents = useSelector(documentsSelectors.document);
  const documentsLoaded = useSelector(documentsSelectors.documentsLoaded);
  const userType = useSelector(appSelectors.userType);
  const [activeDocument, setActiveDocument] =
    useState<APIDocumentSearchResult | null>(null);

  const { loadDocuments, resetDocuments } = useDocuments();

  useEffect(() => {
    if (documents?.[0]) {
      setActiveDocument(documents[0]);
    }
    handleApiResult<APIDocumentSearchResult[]>(
      () =>
        loadDocuments(documentsSection, {
          searchSortOrders: [
            {
              sortField: DocumentFields.PublishedDate,
              sortOrder: APISortOrder.DESC,
            },
          ],
        }),
      ({ result }) => {
        if (result.payload?.[0]) {
          setActiveDocument(result.payload?.[0]);
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDocuments, documentsSection, handleApiResult]);

  useEffect(() => {
    return () => {
      resetDocuments();
    };
  }, [resetDocuments]);

  return !documentsLoaded ? (
    <GlobalLoader />
  ) : (
    <ContentContainer>
      <NavigationContainer>
        <PageHeader variant="h4">{pageName}</PageHeader>
        <StyledList component="nav" aria-labelledby="nested-list-subheader">
          {documents.map((it: APIDocumentSearchResult, index: number) => (
            <ListItemButton
              key={index}
              selected={activeDocument?.document?.id === it.document?.id}
              onClick={() => setActiveDocument(it)}
            >
              <ListItemText
                primary={
                  menuItemRenderer ? menuItemRenderer(it) : it.document?.title
                }
              />
            </ListItemButton>
          ))}
        </StyledList>
      </NavigationContainer>
      <HelpContainer>
        <InnerContent>
          <Suspense fallback={<div>Loading...</div>}>
            {activeDocument && (
              <MarkdownPreview
                source={filterContentByUserType(
                  activeDocument?.document?.text,
                  userType,
                )}
                components={docsComponents as any}
                disableCopy
              />
            )}
          </Suspense>
          {!activeDocument && (
            <EmptyStateContainer>
              <Typography variant="h4" marginBottom="1rem">
                {t('docs.emptyStateText')}
              </Typography>
              <SvgIcon height="50rem" width="50rem" icon="helpcenter" />
            </EmptyStateContainer>
          )}
        </InnerContent>
      </HelpContainer>
    </ContentContainer>
  );
};
